import { ArrowDownIcon } from '@/assets/icons/arrow-down-icon'
import { DatePicker } from '@/components/inputs/date-picker'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { PopoverClose } from '@radix-ui/react-popover'
import { PeriodButton } from './components/period-button'
import { endOfWeek, startOfWeek, subDays } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useCallback, useEffect, useState } from 'react'
import { dateToDDMMYYYY } from '@/utils/date-to-dd-mm-yyy'

interface PeriodFilterProps {
  applyFilter: (startDate: Date, endDate: Date) => void
  startDate: Date | undefined
  setStartDate: (value: Date | undefined) => void
  setEndDate: (value: Date | undefined) => void
  endDate: Date | undefined
}

export const PeriodFilter = ({
  applyFilter,
  startDate,
  setStartDate,
  setEndDate,
  endDate,
}: PeriodFilterProps) => {
  const [buttonText, setButtonText] = useState<string>('')

  const handleStartDate = (date: Date | undefined) => {
    let dateToSet = date
    if (endDate && date) {
      if (date > endDate) dateToSet = endDate
    }

    changePeriod(dateToSet, endDate, `Personalizado`)
  }

  const handleEndDate = (date: Date | undefined) => {
    let dateToSet = date
    if (startDate && date) {
      if (date < startDate) dateToSet = startDate
    }

    changePeriod(startDate, dateToSet, `Personalizado`)
  }

  const changePeriod = useCallback(
    (startDate: Date | undefined, endDate: Date | undefined, text: string) => {
      setStartDate(startDate)
      setEndDate(endDate)
      setButtonText(text)
    },
    [setStartDate, setEndDate],
  )

  useEffect(() => {
    const dateNow = new Date()
    changePeriod(dateNow, dateNow, `Hoje - ${dateToDDMMYYYY(dateNow)}`)
  }, [])

  return (
    <Popover>
      <PopoverTrigger className="rounded-xl cursor-pointer border border-[var(--brand-blue-3)] flex items-center gap-2 bg-muted/40 h-10 px-3 text-[var(--brand-slate-1)]">
        <p className="text-xs font-bold">Período:</p>
        <div className="flex text-base gap-2 items-center font-bold">
          <p>{buttonText}</p>
          <ArrowDownIcon />
        </div>
      </PopoverTrigger>

      <PopoverContent className="w-auto me-12 flex">
        <div className="w-full rounded-lg bg-background p-8 font-semibold flex flex-col gap-5 rounded-lg shadow-centered me-[-5px]">
          <p className="text-base">Períodos</p>
          <div className="flex items-center">
            <div className="w-40">
              <p className="font-bold mb-1">Data Início</p>
              <DatePicker date={startDate} setDate={handleStartDate} />
            </div>
            <p className="font-light mt-6 w-6 text-center">a</p>
            <div className="w-40">
              <p className="font-bold mb-1">Data Fim</p>
              <DatePicker date={endDate} setDate={handleEndDate} />
            </div>
          </div>
          <div className="w-full flex gap-6">
            <PopoverClose className="w-40" asChild>
              <Button className="font-bold" variant={'light'}>
                Cancelar
              </Button>
            </PopoverClose>
            <PopoverClose className="w-40" asChild>
              <Button
                onClick={() => {
                  if (startDate && endDate) {
                    applyFilter(startDate, endDate)
                  }
                }}
                className="font-bold"
              >
                Aplicar
              </Button>
            </PopoverClose>
          </div>
        </div>

        <div className="shrink-1 p-4 ps-5 flex flex-col border border-s-transparent border-[var(--brand-blue-2)] rounded-e-lg">
          <p className="font-medium mb-4">Períodos</p>
          <div className="w-full flex flex-col justify-between h-full">
            <PeriodButton
              startDate={new Date()}
              endDate={new Date()}
              getDatePeriod={(start: Date, end: Date) => {
                changePeriod(start, end, `Hoje - ${dateToDDMMYYYY(start)}`)
              }}
            >
              Hoje
            </PeriodButton>
            <PeriodButton
              startDate={startOfWeek(new Date(), {
                weekStartsOn: 1,
                locale: ptBR,
              })}
              endDate={endOfWeek(new Date(), { weekStartsOn: 1, locale: ptBR })}
              getDatePeriod={(start: Date, end: Date) => {
                changePeriod(start, end, 'Esta semana')
              }}
            >
              Esta semana
            </PeriodButton>
            <PeriodButton
              startDate={subDays(new Date(), 7)}
              endDate={new Date()}
              getDatePeriod={(start: Date, end: Date) => {
                changePeriod(start, end, 'Últimos 7 dias')
              }}
            >
              Últimos 7 dias
            </PeriodButton>
            <PeriodButton
              startDate={subDays(new Date(), 14)}
              endDate={new Date()}
              getDatePeriod={(start: Date, end: Date) => {
                changePeriod(start, end, 'Últimos 14 dias')
              }}
            >
              Últimos 14 dias
            </PeriodButton>
            <PeriodButton
              startDate={subDays(new Date(), 30)}
              endDate={new Date()}
              getDatePeriod={(start: Date, end: Date) => {
                changePeriod(start, end, 'Últimos 30 dias')
              }}
            >
              Últimos 30 dias
            </PeriodButton>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
